import { useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import { useNavigate } from "react-router-dom";
import { SearchResultEvent } from "../../api/fetchEventSearchResults/types";

interface CalendarSession {
  title: string;
  start: string;
  end: string;
  eventUuid: string;
  location: string | null;
  isVirtual: boolean;
}

interface EventDirectoryCalendarProps {
  events: SearchResultEvent[];
}

export default function EventDirectoryCalendar({
  events,
}: EventDirectoryCalendarProps) {
  const navigate = useNavigate();

  const calendarEvents = useMemo(() => {
    const entries: CalendarSession[] = [];

    for (const event of events) {
      for (const session of event.sessions) {
        if (!session.startDate) continue;

        const locationLabel = session.isVirtual
          ? "Virtual"
          : session.location || session.venue || "";

        entries.push({
          title: event.eventName,
          start: session.startDate,
          end: session.endDate || session.startDate,
          eventUuid: event.uuid,
          location: locationLabel,
          isVirtual: session.isVirtual,
        });
      }
    }

    return entries;
  }, [events]);

  return (
    <div className="event-directory-calendar">
      <FullCalendar
        dayMaxEventRows={3}
        eventClick={(info) => {
          info.jsEvent.preventDefault();
          const uuid = info.event.extendedProps.eventUuid;
          if (uuid) {
            navigate(`/event-registration/events/${uuid}`);
          }
        }}
        eventContent={(arg) => {
          const location = arg.event.extendedProps.location;
          return (
            <div className="px-1 py-0.5 overflow-hidden cursor-pointer">
              <p className="text-[11px] font-medium truncate leading-tight text-white">
                {arg.event.title}
              </p>
              {location && (
                <p className="text-[10px] text-blue-100 dark:text-indigo-200 truncate leading-tight">
                  {location}
                </p>
              )}
            </div>
          );
        }}
        events={calendarEvents}
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "",
        }}
        height="auto"
        initialView="dayGridMonth"
        plugins={[dayGridPlugin]}
      />

      <style>{`
        .event-directory-calendar .fc {
          --fc-border-color: var(--color-gray-200);
          --fc-today-bg-color: transparent;
          --fc-neutral-bg-color: transparent;
          --fc-page-bg-color: transparent;
          font-family: inherit;
        }

        :is(.dark) .event-directory-calendar .fc {
          --fc-border-color: rgb(51 65 85);
        }

        .event-directory-calendar .fc .fc-toolbar-title {
          font-size: 1.125rem;
          font-weight: 500;
          color: rgb(17 24 39);
        }

        :is(.dark) .event-directory-calendar .fc .fc-toolbar-title {
          color: rgb(255 255 255);
        }

        .event-directory-calendar .fc .fc-button {
          background: rgb(255 255 255);
          border: 1px solid rgb(229 231 235);
          color: rgb(107 114 128);
          font-size: 0.8125rem;
          padding: 0.25rem 0.75rem;
          border-radius: 0.375rem;
          box-shadow: none;
          font-weight: 400;
        }

        :is(.dark) .event-directory-calendar .fc .fc-button {
          background: rgb(30 41 59);
          border-color: rgb(51 65 85);
          color: rgb(209 213 219);
        }

        .event-directory-calendar .fc .fc-button:hover {
          background: rgb(249 250 251);
          color: rgb(17 24 39);
        }

        :is(.dark) .event-directory-calendar .fc .fc-button:hover {
          background: rgb(51 65 85);
          color: rgb(255 255 255);
        }

        .event-directory-calendar .fc .fc-button-active,
        .event-directory-calendar .fc .fc-button:active {
          background: rgb(243 244 246) !important;
          color: rgb(17 24 39) !important;
          border-color: rgb(209 213 219) !important;
          box-shadow: none !important;
        }

        :is(.dark) .event-directory-calendar .fc .fc-button-active,
        :is(.dark) .event-directory-calendar .fc .fc-button:active {
          background: rgb(51 65 85) !important;
          color: rgb(255 255 255) !important;
          border-color: rgb(71 85 105) !important;
        }

        .event-directory-calendar .fc .fc-col-header-cell {
          padding: 0.5rem 0;
          font-size: 0.75rem;
          font-weight: 400;
          color: rgb(107 114 128);
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }

        :is(.dark) .event-directory-calendar .fc .fc-col-header-cell {
          color: rgb(156 163 175);
        }

        .event-directory-calendar .fc .fc-daygrid-day-number {
          font-size: 0.8125rem;
          color: rgb(107 114 128);
          padding: 0.375rem 0.5rem;
        }

        :is(.dark) .event-directory-calendar .fc .fc-daygrid-day-number {
          color: rgb(156 163 175);
        }

        .event-directory-calendar .fc .fc-day-today .fc-daygrid-day-number {
          background: rgb(17 24 39);
          color: rgb(255 255 255);
          border-radius: 9999px;
          width: 1.5rem;
          height: 1.5rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          margin: 0.25rem;
        }

        :is(.dark) .event-directory-calendar .fc .fc-day-today .fc-daygrid-day-number {
          background: rgb(255 255 255);
          color: rgb(17 24 39);
        }

        .event-directory-calendar .fc .fc-daygrid-event {
          background: rgb(59 130 246);
          border: none;
          border-radius: 0.375rem;
          margin: 1px 2px;
          color: rgb(255 255 255);
        }

        :is(.dark) .event-directory-calendar .fc .fc-daygrid-event {
          background: rgb(99 102 241);
          color: rgb(255 255 255);
        }

        .event-directory-calendar .fc .fc-daygrid-event:hover {
          background: rgb(37 99 235);
        }

        :is(.dark) .event-directory-calendar .fc .fc-daygrid-event:hover {
          background: rgb(79 70 229);
        }

        .event-directory-calendar .fc .fc-more-link {
          font-size: 0.75rem;
          color: rgb(107 114 128);
          font-weight: 400;
          padding: 0 0.25rem;
        }

        :is(.dark) .event-directory-calendar .fc .fc-more-link {
          color: rgb(156 163 175);
        }

        .event-directory-calendar .fc .fc-day-other .fc-daygrid-day-number {
          opacity: 0.4;
        }
      `}</style>
    </div>
  );
}
